import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronLeft, RefreshCw, Copy, Search, ShieldAlert, KeyRound, Download } from 'lucide-react';
import * as XLSX from 'xlsx';
import { appLicenseApi, AppLicense, AppLicenseCheck, AppLicenseSummary } from '../services/api';
import { useToast } from '../components/Toast/Toast';

// Licences for ABS WhatsApp Sender, kept against the Tally serial. Staff can issue
// for a serial that is Our Tally; anything else is admin-only. The server decides
// that from tally_status — this page only explains the answer. The server also
// sets the expiry from Tally, and allows one licence while a serial is not ours.

const KIND_LABEL: Record<AppLicenseCheck['kind'], string> = {
  our: 'Our Tally',
  not_our: 'Not Our Tally',
  unknown: 'Not in the portal',
};
const KIND_STYLE: Record<AppLicenseCheck['kind'], string> = {
  our: 'bg-green-50 text-green-700 border-green-200',
  not_our: 'bg-amber-50 text-amber-700 border-amber-200',
  unknown: 'bg-slate-100 text-slate-600 border-slate-200',
};

const day = (v: string | null) => (v ? String(v).slice(0, 10) : 'No expiry');
const ddmmyyyy = (v: string) => v.split('-').reverse().join('-');
const when = (v: string) => new Date(v).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
// Older rows have no serial_kind: their stored Tally status is all there is.
const kindOf = (r: AppLicense): AppLicenseCheck['kind'] => r.serial_kind || (r.tally_status === 'Our Tally' ? 'our' : 'not_our');
const isExpired = (r: AppLicense) => !!r.expires_on && String(r.expires_on).slice(0, 10) < new Date().toISOString().slice(0, 10);

const AppLicenses: React.FC = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  // The customer profile links here with ?serial=, against each of its serials.
  const [searchParams] = useSearchParams();
  const linkedSerial = (searchParams.get('serial') || '').trim();
  const [serial, setSerial] = useState(linkedSerial);
  const [check, setCheck] = useState<AppLicenseCheck | null>(null);
  const [machine, setMachine] = useState('');
  const [expires, setExpires] = useState('');
  const [customer, setCustomer] = useState('');
  const [busy, setBusy] = useState(false);
  const [issued, setIssued] = useState<string | null>(null);
  const [rows, setRows] = useState<AppLicense[]>([]);
  const [search, setSearch] = useState('');
  const [summary, setSummary] = useState<AppLicenseSummary | null>(null);

  const loadList = useCallback(async (q?: string) => {
    try {
      const res = await appLicenseApi.list(q);
      setRows(res.data || []);
      setSummary((await appLicenseApi.summary()).data);
    } catch (e: any) {
      showError('Licences', e?.message || 'Could not load the licences');
    }
  }, [showError]);

  useEffect(() => { loadList(); }, [loadList]);

  const runCheck = useCallback(async (value: string) => {
    setIssued(null);
    setCheck(null);
    if (!value) return;
    setBusy(true);
    try {
      const res = await appLicenseApi.check(value);
      setCheck(res.data);
    } catch (err: any) {
      showError('Serial', err?.message || 'Could not check this serial');
    }
    setBusy(false);
  }, [showError]);

  useEffect(() => { if (linkedSerial) runCheck(linkedSerial); }, [linkedSerial, runCheck]);

  const generate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!check) return;
    setBusy(true);
    try {
      const res = await appLicenseApi.generate({
        serial: check.serial, machine_id: machine, expires_on: expires || undefined, customer_name: customer || undefined,
      });
      setIssued(res.data.license_key);
      setMachine('');
      setExpires('');
      setCustomer('');
      showSuccess('Licence generated', `For ${res.data.customer_name}`);
      loadList(search);
      const again = await appLicenseApi.check(check.serial);
      setCheck(again.data);
    } catch (err: any) {
      showError('Licence', err?.message || 'Could not generate the licence');
    }
    setBusy(false);
  };

  const copy = (text: string) => {
    navigator.clipboard.writeText(text)
      .then(() => showSuccess('Copied', 'Licence key copied'))
      .catch(() => showError('Copy', 'Could not copy. Select the key and copy it by hand.'));
  };

  const exportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(rows.map((r) => ({
      Serial: r.tally_serial,
      Customer: r.customer_name,
      'Customer ID': r.customer_id ?? '',
      'Status when issued': KIND_LABEL[kindOf(r)],
      'Machine ID': r.machine_id,
      Expiry: day(r.expires_on),
      'Issued by': r.issued_by_name || '',
      'Issued on': when(r.created_at),
      'Licence key': r.license_key,
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Licences');
    XLSX.writeFile(wb, 'App_Licences.xlsx');
  };

  const input = 'w-full border border-slate-300 rounded-lg px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-200';

  return (
    <div className="h-full flex flex-col bg-slate-50">
      <div className="bg-white border-b border-slate-200 px-4 py-2.5 flex items-center gap-2">
        <button onClick={() => navigate(-1)} className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100" aria-label="Back"><ChevronLeft size={18} /></button>
        <KeyRound size={18} className="text-blue-600" />
        <h1 className="text-[15px] font-bold text-slate-800">App Licences</h1>
        <button onClick={() => loadList(search)} className="ml-auto p-1.5 rounded-lg text-slate-500 hover:bg-slate-100" title="Refresh"><RefreshCw size={15} /></button>
      </div>

      <div className="flex-1 overflow-auto p-4">
        <div className="max-w-4xl mx-auto space-y-4">
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="text-[13px] font-bold text-slate-700 mb-2">Generate a licence for ABS WhatsApp Sender</div>
            <form onSubmit={(e) => { e.preventDefault(); runCheck(serial.trim()); }} className="flex gap-2">
              <input className={input} value={serial} onChange={(e) => setSerial(e.target.value)} placeholder="Tally serial number" aria-label="Tally serial number" />
              <button type="submit" disabled={busy || !serial.trim()}
                className="inline-flex items-center gap-1.5 text-[13px] font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-lg px-4 py-2 disabled:opacity-50">
                <Search size={14} /> Check
              </button>
            </form>

            {check && (
              <div className="mt-4">
                <div className="flex items-center gap-2 flex-wrap text-[13px]">
                  <span className={`border rounded-full px-2.5 py-0.5 text-[12px] font-bold ${KIND_STYLE[check.kind]}`}>{KIND_LABEL[check.kind]}</span>
                  <span className="font-semibold text-slate-800">{check.serial}</span>
                  {check.customer_name && <span className="text-slate-600">{check.customer_name}</span>}
                </div>

                {!check.can_generate && (
                  <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3 text-[13px] text-amber-900 flex gap-2">
                    <ShieldAlert size={16} className="shrink-0 mt-0.5" /> {check.reason}
                  </div>
                )}

                {check.can_generate && (
                  <form onSubmit={generate} className="mt-3 grid gap-3 sm:grid-cols-2">
                    <label className="text-[12px] font-semibold text-slate-600">Machine ID (from the app's Licensing screen)
                      <input className={`${input} mt-1 font-mono`} value={machine} onChange={(e) => setMachine(e.target.value)} placeholder="89C32-FA1FE-3ACC6-9B10B" required />
                    </label>
                    {check.kind === 'unknown' ? (
                      <label className="text-[12px] font-semibold text-slate-600">Expiry date (blank for no expiry)
                        <input className={`${input} mt-1`} type="date" value={expires} onChange={(e) => setExpires(e.target.value)} />
                      </label>
                    ) : (
                      <label className="text-[12px] font-semibold text-slate-600">Expiry date (from Tally)
                        <input className={`${input} mt-1 bg-slate-100 text-slate-700`} value={check.tally_expiry ? ddmmyyyy(check.tally_expiry) : ''} readOnly />
                      </label>
                    )}
                    {!check.customer_name && (
                      <label className="text-[12px] font-semibold text-slate-600 sm:col-span-2">Customer name
                        <input className={`${input} mt-1`} value={customer} onChange={(e) => setCustomer(e.target.value)} required />
                      </label>
                    )}
                    <div className="sm:col-span-2 flex items-center gap-3 flex-wrap">
                      <button type="submit" disabled={busy}
                        className="inline-flex items-center gap-1.5 text-[13px] font-bold text-white bg-green-600 hover:bg-green-700 rounded-lg px-4 py-2 disabled:opacity-50">
                        <KeyRound size={14} /> {busy ? 'Working…' : 'Generate licence'}
                      </button>
                      <span className="text-[12px] text-slate-500">{check.kind === 'our'
                        ? 'A key cannot be cancelled once issued.'
                        : 'A key cannot be cancelled once issued. This serial gets only one licence until it becomes Our Tally.'}</span>
                    </div>
                  </form>
                )}

                {check.licenses.length > 0 && (
                  <div className="mt-3 text-[12.5px] text-slate-600">
                    <div className="font-semibold text-slate-700 mb-1">Already issued for this serial</div>
                    {check.licenses.map((l) => (
                      <div key={l.id} className="flex items-center gap-3 flex-wrap py-1 border-t border-slate-100">
                        <span className="font-mono text-[11.5px]">{l.machine_id}</span>
                        <span>{day(l.expires_on)}</span>
                        <button onClick={() => copy(l.license_key)} className="ml-auto inline-flex items-center gap-1 text-[12px] font-bold text-blue-700 hover:underline"><Copy size={12} /> Copy key</button>
                      </div>
                    ))}
                  </div>
                )}

                {issued && (
                  <div className="mt-3 bg-green-50 border border-green-200 rounded-lg p-3">
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <span className="text-[13px] font-bold text-green-800">Send this key to the customer</span>
                      <button onClick={() => copy(issued)} className="inline-flex items-center gap-1 text-[12px] font-bold text-green-800 border border-green-300 bg-white rounded-lg px-2.5 py-1"><Copy size={12} /> Copy</button>
                    </div>
                    <div className="font-mono text-[11.5px] break-all text-slate-700 select-all">{issued}</div>
                  </div>
                )}
              </div>
            )}
          </div>

          {summary && (
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              {([['Licences issued', summary.total], ['Serials', summary.serials], ['Our Tally', summary.our], ['Not Our Tally', summary.not_our], ['Expired', summary.expired]] as [string, number][]).map(([label, value]) => (
                <div key={label} className="bg-white border border-slate-200 rounded-xl px-4 py-2.5">
                  <div className="text-[11.5px] font-semibold text-slate-500">{label}</div>
                  <div className="text-[20px] font-bold text-slate-800">{value}</div>
                </div>
              ))}
            </div>
          )}

          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="px-4 py-2.5 border-b border-slate-200 flex items-center gap-3 flex-wrap">
              <span className="text-[13px] font-bold text-slate-700">Licences issued</span>
              <button onClick={exportExcel} disabled={!rows.length} className="ml-auto inline-flex items-center gap-1 text-[12px] font-bold text-slate-700 border border-slate-300 rounded-lg px-2.5 py-1.5 hover:bg-slate-50 disabled:opacity-50"><Download size={13} /> Excel</button>
              <form className="w-64 max-w-full" onSubmit={(e) => { e.preventDefault(); loadList(search); }}>
                <input className={input} value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search serial, customer, Machine ID" aria-label="Search licences" />
              </form>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[12.5px]">
                <thead className="bg-slate-50 text-slate-500">
                  <tr>
                    {['Serial', 'Customer', 'Status when issued', 'Machine ID', 'Expiry', 'Issued by', 'Issued on', ''].map((h) => (
                      <th key={h} className="text-left px-4 py-2 font-semibold whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-t border-slate-100">
                      <td className="px-4 py-2 whitespace-nowrap font-semibold text-slate-700">{r.tally_serial}</td>
                      <td className="px-4 py-2 text-slate-700">{r.customer_name}</td>
                      <td className="px-4 py-2 whitespace-nowrap"><span className={`border rounded-full px-2 py-0.5 text-[11px] font-bold ${KIND_STYLE[kindOf(r)]}`}>{KIND_LABEL[kindOf(r)]}</span></td>
                      <td className="px-4 py-2 whitespace-nowrap font-mono text-[11.5px]">{r.machine_id}</td>
                      <td className={`px-4 py-2 whitespace-nowrap ${isExpired(r) ? 'text-red-600 font-semibold' : ''}`}>{day(r.expires_on)}</td>
                      <td className="px-4 py-2 whitespace-nowrap">{r.issued_by_name || '–'}</td>
                      <td className="px-4 py-2 whitespace-nowrap">{when(r.created_at)}</td>
                      <td className="px-4 py-2 text-right">
                        <button onClick={() => copy(r.license_key)} className="inline-flex items-center gap-1 text-[12px] font-bold text-blue-700 hover:underline"><Copy size={12} /> Copy key</button>
                      </td>
                    </tr>
                  ))}
                  {!rows.length && <tr><td colSpan={8} className="px-4 py-6 text-center text-slate-500">No licences yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppLicenses;
